import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '../tools/makeStyles';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import SpeechBubble from './SpeechBubble.jsx';

const ENTER = 'Enter';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
  },
  conversationsContainer: {
    boxSizing: 'border-box',
    padding: theme.spacing(2, 2, 0, 2),
    flex: 1,
    overflowY: 'auto',
  },
  speechBubbleContainer: {
    padding: '8px 0px',
  },
  textFieldContainer: {
    boxSizing: 'border-box',
  },
  sendIcon: {
    width: '10%',
  },
}));

const checkCurrentUser = (currentUserId, user) => {
  const hasCurrentAnonymousUser = !currentUserId && !user;
  const hasCurrentUser = currentUserId && user && currentUserId === user.id;

  return hasCurrentAnonymousUser || hasCurrentUser;
};

const defaultAnonymousUser = {
  name: 'Anonymous',
  avatarURL: '',
};

const textColor = '#333333';
const userStyle = { backgroundColor: '#A5FCEC', color: textColor };
const anonymousUserStyle = { backgroundColor: '#0BDAD2', color: textColor };

const Bubbles = ({ messages, currentUserId, anonymousUser, className }) => {
  if (!messages || !messages.length) {
    return <div>No conversations!</div>;
  }

  return messages.map((conversation) => {
    const isCurrentUser = checkCurrentUser(currentUserId, conversation.user);
    const user = conversation.user || anonymousUser;
    const bubbleStyle = user === anonymousUser ? anonymousUserStyle : userStyle;

    return (
      <div key={conversation.id} className={className}>
        <SpeechBubble
          text={conversation.text}
          isCurrentUser={isCurrentUser}
          style={bubbleStyle}
          user={user}
          created={conversation.created}
        />
      </div>
    );
  });
};

/**
 * Conversations
 *
 * @param messages    | array | []       | An array of messages                                                             |
 * @param handleEnter | func  | () => {} | Handler function coming from the parent component to handle onClicks and onEnter |
 */
const Conversations = ({
  messages,
  currentUserId,
  anonymousUser = defaultAnonymousUser,
  placeholder = 'Type a message...',
  handleEnter = () => {},
}) => {
  Conversations.propTypes = {
    messages: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        text: PropTypes.string.isRequired,
        created: PropTypes.string.isRequired,
        user: PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
          avatarURL: PropTypes.string,
        }),
      }),
    ),
    currentUserId: PropTypes.number,
    anonymousUser: PropTypes.shape({
      name: PropTypes.string.isRequired,
      avatarURL: PropTypes.string,
    }),
    placeholder: PropTypes.string.isRequired,
    handleEnter: PropTypes.func.isRequired,
  };

  const classes = useStyles();
  const messagesBoxRef = useRef(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const messagesBoxElm = messagesBoxRef.current;
    if (!messagesBoxElm || messagesBoxElm.scrollTop == null) {
      return;
    }

    messagesBoxElm.scrollTop = messagesBoxElm.scrollHeight;
  }, [messages]);

  const handleKeyPress = (e) => {
    if (e.key !== ENTER) {
      return;
    }

    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      return;
    }

    handleEnter(trimmedMessage);
    setMessage('');
  };

  return (
    <div className={classes.root}>
      <div className={classes.conversationsContainer} ref={messagesBoxRef}>
        <Bubbles
          messages={messages}
          currentUserId={currentUserId}
          className={classes.speechBubbleContainer}
          anonymousUser={anonymousUser}
        />
      </div>

      <div className={classes.textFieldContainer}>
        <TextField
          onKeyPress={handleKeyPress}
          id="input-field"
          label={placeholder}
          margin="none"
          variant="outlined"
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          InputProps={{
            endAdornment: (
              <IconButton onClick={() => handleKeyPress({ key: ENTER })} size="large">
                <SendIcon />
              </IconButton>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default Conversations;
